import * as React from 'react';
import { camelCase, snakeCase } from 'lodash';
import { Trans } from 'react-i18next';
import { MasterdataEntity } from './types.ts';
import { SEARCH_MASTERDATA_ENTITY_FIELD } from './queries.ts';
import { RESULTS_PAGE_SIZE } from './components/MasterDataBrowser.tsx';
import { format } from 'date-fns';

// We don't have a dedicated component here, this just contains all the logic to generate the props that
// are passed into the rendered field component

export const handleOpenMasterdataBrowser = ({
    handlers,
    partitionId,
    isPartitionIdField,
    searchTerm,
    lookupType,
    lookupDefinitionId,
    extraSearchTerms,
    targetField,
    fieldProps = null,
    onSelectRecord = null,
    hasMatching = false,
    matchingDocumentNumber = null,
    lookupLastSuccessfulImportDate = null,
    extractionSearchResults = null,
}) => {
    const { openMasterdataBrowser, closeMasterdataBrowser } = handlers;

    openMasterdataBrowser({
        searchTerm: searchTerm,
        lookupType: lookupType,
        lookupDefinitionId: lookupDefinitionId,
        partitionId: partitionId,
        extraSearchTerms: extraSearchTerms,
        targetField: targetField,
        extractionSearchResults: extractionSearchResults,
        dropdownMessageBuilder: createDropdownMessageBuilder({
            targetField: targetField,
            lookupType: lookupType,
            partitionId: partitionId,
            isPartitionIdField: isPartitionIdField,
            hasMatching: hasMatching,
            matchingDocumentNumber: matchingDocumentNumber,
            lookupLastSuccessfulImportDate: lookupLastSuccessfulImportDate,
        }),
        searchMessageBuilder: createSearchMessageBuilder({
            targetField: targetField,
            lookupType: lookupType,
            hasMatching: hasMatching,
            matchingDocumentNumber: matchingDocumentNumber,
        }),
        onSelectRecord:
            onSelectRecord ||
            ((record) => {
                const targetFieldValue = record[camelCase(targetField)];
                // Call the field's update handler & optimistically close the modal (since we don't have a promise
                // to attach to)
                fieldProps.onUpdate?.(targetFieldValue, {
                    _lookup_id: record?.id,
                    _lookupEntity: record,
                });
                closeMasterdataBrowser();
            }),
    });
};

const createMatchingDropdownMessage = ({
    t,
    inputValue,
    resultItems,
    matchingDocumentNumber,
    lookupLastSuccessfulImportDate,
    lookupType,
}) => {
    if (!matchingDocumentNumber) {
        return (
            <Trans
                t={t}
                i18nKey={
                    lookupType === 'OfferMatchingLookupType'
                        ? `masterdata:assistance.fields.autocompleteField.missingDocumentNumberOffer`
                        : `masterdata:assistance.fields.autocompleteField.missingDocumentNumber`
                }
            />
        );
    } else if (resultItems.length == 0) {
        return (
            <Trans
                t={t}
                i18nKey={
                    lookupType === 'OfferMatchingLookupType'
                        ? `masterdata:assistance.fields.autocompleteField.noResultsMatchingOffer`
                        : `masterdata:assistance.fields.autocompleteField.noResultsMatching`
                }
                values={{ searchValue: inputValue, updatedAt: lookupLastSuccessfulImportDate }}
                count={inputValue ? inputValue.length : 0}
            />
        );
    }
};

export const createDropdownMessageBuilder =
    ({
        isPartitionIdField,
        partitionId,
        lookupType,
        targetField,
        hasMatching = false,
        matchingDocumentNumber = null,
        lookupLastSuccessfulImportDate = null,
    }) =>
    (t, inputValue, resultItems) => {
        // Create dropdown message when document matching is enabled
        if (hasMatching) {
            return createMatchingDropdownMessage({
                t: t,
                inputValue: inputValue,
                resultItems: resultItems,
                matchingDocumentNumber: matchingDocumentNumber,
                lookupLastSuccessfulImportDate: lookupLastSuccessfulImportDate,
                lookupType: lookupType,
            });
        }

        // TODO: show a warning here if no lookup_definition_id is configured for the field

        if (resultItems.length == 0) {
            return (
                <Trans
                    t={t}
                    i18nKey={'browser.searchFilterInfo_zero'}
                    values={{ searchTerm: inputValue, fieldName: t(`schema.${lookupType}.${camelCase(targetField)}`) }}
                />
            );
        }
    };

const createSearchMessageBuilder =
    ({ targetField, lookupType, hasMatching = false, matchingDocumentNumber = null }) =>
    (t, inputValue, resultItems, maxResultsShown) => {
        let searchTranslation = 'searchFilterInfo';
        if (hasMatching) {
            searchTranslation += 'Matching';
        }

        return (
            <Trans
                t={t}
                i18nKey={'browser.' + searchTranslation + (resultItems.length >= maxResultsShown ? '_max' : '')}
                values={{
                    numResults: resultItems.length,
                    searchTerm: inputValue,
                    fieldName: t(`schema.${lookupType}.${camelCase(targetField)}`),
                    documentNumber: matchingDocumentNumber,
                }}
                count={resultItems.length}
            />
        );
    };
